/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/camelcase */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Portal from 'react-portal';
import AutoComplete from 'react-autocomplete';
import Highlighter from 'react-highlight-words';
import { HotKeys } from 'react-hotkeys';
import cn from 'classnames';
import _pick from 'lodash/pick';
import _delay from 'lodash/delay';
import TextCropper from '@omilia/text-cropper';

import { intentType } from 'types';
import Button from 'components/Button';
import Spinner from 'components/Spinner';

import { getConstraintSet } from './utils';

import './style.scss';

const keyMap = {
  leave: ['esc'],
};

export default class MapToIntentPopup extends Component {
  handleHotKeys = {
    leave: e => this.handleLeave(e),
  };

  static propTypes = {
    entry: PropTypes.shape({
      id: PropTypes.string,
      matched: PropTypes.shape({
        annotations: PropTypes.array,
        final_features: PropTypes.array,
      }),
      order: PropTypes.number,
      value: PropTypes.string,
    }),
    intents: PropTypes.arrayOf(intentType),

    isBelowIcon: PropTypes.bool,
    isLoading: PropTypes.bool,
    position: PropTypes.shape({
      left: PropTypes.string,
      top: PropTypes.string,
    }),
    onClose: PropTypes.func,
    onGetAllIntents: PropTypes.func,
    onGetIntent: PropTypes.func,
    onGoToIntent: PropTypes.func,
    onOpenNewIntentModal: PropTypes.func,
    onUpdateIntent: PropTypes.func,
  };

  state = {
    isOpen: false,
    searchTerm: '',
  };

  handleOpen = () => {
    this.setState({ isOpen: true }, () => this.input.focus());
  };

  handleClose = () => {
    _delay(() => {
      if (this.state.isOpen) {
        this.setState({ isOpen: false, searchTerm: '' });
      }
    }, 100);
  };

  matchItemToTerm = (item, value) => {
    if (value === '') {
      return true;
    }
    return item.name.toLowerCase().includes(value.toLowerCase());
  };

  handleGetItemValue = option => option.name;

  handleChange = value => {
    this.setState(
      {
        searchTerm: value,
      },
      () => {
        this.props.onGetAllIntents({
          name__icontains: value,
        });
      },
    );
  };

  handleLeave = e => {
    e.cancelBubble = true;
    if (this.state.searchTerm !== '') {
      this.setState({ searchTerm: '' });
    } else if (this.state.searchTerm === '' && this.state.isOpen) {
      this.setState({ isOpen: false });
    } else {
      this.props.onClose();
    }
  };

  /* eslint-disable camelcase */
  handleSelect = intent => {
    const { annotations, final_features } = this.props.entry.matched;
    const { id, name, constraint_sets } = intent;
    const constraintSet = getConstraintSet(
      annotations,
      final_features,
      constraint_sets.length + 1,
    );
    this.setState(
      {
        searchTerm: name,
        isOpen: false,
      },
      async () => {
        this.props.onClose();
        await this.props.onUpdateIntent(id, {
          ..._pick(intent, ['name', 'description', 'color', 'enabled']),
          constraint_sets: [...constraint_sets, constraintSet],
        });

        await this.props.onGetIntent(id);
        this.props.onGoToIntent(id);
      },
    );
  };

  handleOpenModal = () => {
    const { annotations, final_features } = this.props.entry.matched;
    const constraintSet = getConstraintSet(annotations, final_features, 1);

    this.setState({ isOpen: false, searchTerm: '' }, () => {
      this.props.onOpenNewIntentModal(constraintSet);
      this.props.onClose();
    });
  };

  handleRef = ref => {
    this.input = ref;
  };

  renderInput = props => {
    const { isOpen } = this.state;
    return (
      <HotKeys handlers={this.handleHotKeys} keyMap={keyMap}>
        <div
          data-test="map-to-intent-search-container"
          styleName="search-container"
        >
          {!isOpen && (
            <button type="button" onClick={this.handleOpen}>
              Search Intent
            </button>
          )}
          <input
            data-test="search-input"
            placeholder="Search Intent"
            styleName="input"
            {...props}
          />

          {this.props.isLoading && isOpen && (
            <Spinner isBlue styleName="spinner" />
          )}
        </div>
      </HotKeys>
    );
  };

  renderItem = (item, isHighlighted) => (
    <div
      key={item.id}
      data-test="map-to-intent-item"
      styleName={cn('item', {
        'is-active': isHighlighted,
      })}
    >
      <TextCropper placement="right" styleName="cropper" text={item.name}>
        <Highlighter
          autoEscape
          searchWords={[this.state.searchTerm]}
          textToHighlight={item.name}
        />
      </TextCropper>
    </div>
  );

  renderNoItems = () => {
    if (this.props.isLoading) {
      return null;
    }

    return <span styleName="item no-items">No intents found</span>;
  };

  render() {
    const { searchTerm, isOpen } = this.state;
    const { position, onClose, intents, isBelowIcon } = this.props;

    return (
      <Portal closeOnEsc closeOnOutsideClick isOpened onClose={onClose}>
        <div
          data-test="map-to-intent-window"
          style={position}
          styleName={cn('root', { 'is-below-of-icon': isBelowIcon })}
        >
          <AutoComplete
            ref={this.handleRef}
            autoHighlight
            getItemValue={this.handleGetItemValue}
            inputProps={{
              onBlur: this.handleClose,
            }}
            items={intents}
            open={isOpen}
            renderInput={this.renderInput}
            renderItem={this.renderItem}
            renderMenu={children => (
              <div data-test="map-to-intent-menu" styleName="menu">
                {children.length > 0 ? children : this.renderNoItems()}
              </div>
            )}
            shouldItemRender={this.matchItemToTerm}
            value={searchTerm}
            wrapperStyle={{ display: 'block' }}
            onChange={(event, value) => this.handleChange(value)}
            onSelect={(value, item) => this.handleSelect(item)}
          />

          <Button
            isFull
            isSmall
            data-test="create"
            type="button"
            onClick={this.handleOpenModal}
          >
            <i className="icon-add" />
            <span>Create Intent</span>
          </Button>
        </div>
      </Portal>
    );
  }
}
